import { z } from "zod";
import { AttachmentRequirementDisplaySchema } from "@/services/shared/attachment-requirement.types";
import {
	ColumnValueSchema,
	DataQuerySchema,
	FormDefinitionSchema,
} from "../form-definition/form-definition-core.types";
import {
	SimpleAddressSchema,
	SubOrganizationDisplaySchema,
} from "../organization/organization.types";
import { UserInfoLightSchema } from "../shared/user-info-light.types";

export {
	type CreateNoteInfo,
	CreateNoteInfoSchema,
	type JobNoteParams,
	JobNoteParamsSchema,
	type NoteDisplay,
	NoteDisplayArraySchema,
	NoteDisplaySchema,
} from "../shared/note.types";
export {
	type UserInfoLight,
	UserInfoLightSchema,
} from "../shared/user-info-light.types";

// ==================== BASE SCHEMAS ====================

/**
 * Job status schema
 */
export const JobStatusSchema = z.object({
	code: z.number().int(),
	name: z.string().nullable(),
	description: z.string().nullable(),
});

/**
 * Job status display schema (for /jobs/statuses endpoint)
 */
export const JobStatusDisplaySchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable(),
	description: z.string().nullable(),
	isInitialStatus: z.boolean().optional(),
});

/**
 * Job statuses array schema
 */
export const JobStatusesArraySchema = z.array(JobStatusDisplaySchema);

/**
 * Bounding box schema for geographic filtering
 */
export const BoundingBoxSchema = z.object({
	MaxLatitude: z.number().optional(),
	MinLatitude: z.number().optional(),
	MaxLongitude: z.number().optional(),
	MinLongitude: z.number().optional(),
});

/**
 * Nested `statusLifecycle` on GET /jobs/types/{id} (and list rows when present).
 * API sends full workflow object; we keep known fields and allow the rest via passthrough.
 */
const JobTypeEmbeddedStatusLifecycleSchema = z
	.object({
		id: z.string().uuid(),
		name: z.string().nullable().optional(),
		organizationId: z.string().uuid().nullable().optional(),
	})
	.passthrough();

/**
 * OpenAPI `JobTypeDisplay` — GET /jobs/types/{start}/{count}, GET /jobs/types/{id}
 *
 * Preprocess:
 * - Normalizes misspelled `jobTypeSubOrganizaitons` from the API.
 * - Denormalizes `lifecycleId` / `lifecycleName` from nested `statusLifecycle` when the API
 *   only returns the embedded object (see OpenAPI / sample payloads).
 */
const JobTypeDisplayCoreSchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	jobTypeSubOrganizations: z.array(SubOrganizationDisplaySchema).default([]),
	statusLifecycle: JobTypeEmbeddedStatusLifecycleSchema.nullable().optional(),
	/** Flat fields when API sends them; otherwise filled from `statusLifecycle` in preprocess */
	lifecycleId: z.string().nullable().optional(),
	lifecycleName: z.string().nullable().optional(),
	/** Denormalized from API for list/detail display */
	statusLifecycleName: z.string().nullable().optional(),
	source: z.string().nullable().optional(),
	/** When true, job type is a system/default type; when false, custom */
	defaultType: z.boolean().optional(),
	/** Whether this type is enabled for the organization */
	enabled: z.boolean().optional(),
	createdAt: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
	createdBy: z.string().uuid().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	/** Display name supplied directly by the backend — no secondary lookup needed */
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
	locationRequired: z.boolean().optional(),
	attachmentRequirement: AttachmentRequirementDisplaySchema.nullable().optional(),
	hasForm: z.boolean().optional(),
});

export const JobTypeDisplaySchema = z.preprocess((val) => {
	if (val && typeof val === "object" && !Array.isArray(val)) {
		const o = val as Record<string, unknown>;
		const rawSubs = o.jobTypeSubOrganizaitons ?? o.jobTypeSubOrganizations;
		const embedded = o.statusLifecycle;
		const embeddedId =
			embedded &&
			typeof embedded === "object" &&
			embedded !== null &&
			typeof (embedded as { id?: unknown }).id === "string"
				? (embedded as { id: string }).id
				: undefined;
		const embeddedName =
			embedded && typeof embedded === "object" && embedded !== null
				? ((embedded as { name?: string | null }).name ?? undefined)
				: undefined;

		const lifecycleId =
			typeof o.lifecycleId === "string" ? o.lifecycleId : embeddedId;

		const lifecycleName =
			(o.lifecycleName as string | null | undefined) ?? embeddedName;

		return {
			...o,
			jobTypeSubOrganizations: Array.isArray(rawSubs) ? rawSubs : [],
			...(lifecycleId !== undefined ? { lifecycleId } : {}),
			...(lifecycleName !== undefined ? { lifecycleName } : {}),
		};
	}
	return val;
}, JobTypeDisplayCoreSchema);

/** @deprecated Use JobTypeDisplaySchema — kept as alias for existing imports */
export const JobTypeSchema = JobTypeDisplaySchema;

/**
 * Create Job Type Info schema
 * Based on POST /jobs/types request body
 */
export const CreateJobTypeInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	subOrganizationIds: z.array(z.string()).nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	/** Sent to POST /jobs/types; backend may add `lifecycleId` to OpenAPI when ready */
	statusLifecycleId: z.string().nullable().optional(),
});

/**
 * Job types array schema (JobTypeDisplay[])
 */
export const JobTypesArraySchema = z.array(JobTypeDisplaySchema);

/**
 * OpenAPI `EditJobTypeInfo` — PUT /jobs/types/{jobTypeId}
 */
export const EditJobTypeInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	subOrganizationIds: z.array(z.string()).nullable().optional(),
	typeId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	statusLifecycleId: z.string().nullable().optional(),
	locationRequired: z.boolean().optional(),
});

/**
 * Location schema for geographic filtering
 * API includes type (e.g. 0 for point) and coordinates.
 */
export const LocationSchema = z.object({
	type: z.number().int().optional(),
	coordinates: z.object({
		altitude: z.number().optional().nullable(),
		latitude: z.number().optional().nullable(),
		longitude: z.number().optional().nullable(),
	}),
	boundingBoxes: z.array(BoundingBoxSchema).optional().nullable(),
	crs: z.string().optional().nullable(),
});

/**
 * User reference schema for createdBy/updatedBy fields
 */
export const JobUserReferenceSchema = z.object({
	id: z.string().uuid(),
	displayName: z.string().nullable().optional(),
	email: z.string().nullable().optional(),
	firstName: z.string().nullable().optional(),
	middleName: z.string().nullable().optional(),
	lastName: z.string().nullable().optional(),
});

/**
 * Job Display schema
 * Based on API response from GET /jobs/{id} and GET /jobs/{start}/{count}
 */
export const JobDisplaySchema = z.object({
	createdBy: JobUserReferenceSchema.nullable().optional(),
	updatedBy: JobUserReferenceSchema.nullable().optional(),
	id: z.string().uuid(),
	organizationId: z.string().uuid().nullable(),
	jobNumber: z.number().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	statusId: z.string().uuid().nullable(),
	statusName: z.string().nullable().optional(),
	statusCategoryName: z.string().nullable().optional(),
	statusCategoryIcon: z.string().nullable().optional(),
	statusCategoryIconColor: z.string().nullable().optional(),
	typeId: z.string().uuid().nullable(),
	typeName: z.string().nullable().optional(),
	typeIcon: z.string().nullable().optional(),
	subOrganizationId: z.string().uuid().nullable(),
	location: LocationSchema.nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	recordingDeviceData: z.unknown().nullable().optional(),
	entityAttachmentRequirementId: z.string().uuid().nullable().optional(),
	userAssignmentCount: z.number().int().optional(),
	subContractorCount: z.number().int().optional(),
	createdAt: z.string().datetime().nullable(),
	createdAtOffset: z.string().nullable(),
	updatedAt: z.string().datetime().nullable(),
	updatedAtOffset: z.string().nullable(),
	data: z.array(ColumnValueSchema).nullable().optional(),
	/** Opaque job-type form values (OpenAPI JobDisplay.formData). */
	formData: z
		.union([z.record(z.string(), z.unknown()), z.string()])
		.nullable()
		.optional(),
	formDefinition: FormDefinitionSchema.nullable().optional(),
	/**
	 * Related assets (OpenAPI JobDisplay.assets → AssetDisplay[]).
	 * Typed loosely to avoid a job↔asset circular import.
	 */
	assets: z.array(z.unknown()).nullable().optional(),
	/**
	 * Related records (OpenAPI JobDisplay.records → RecordDisplay[]).
	 * Typed loosely to avoid a job↔record circular import.
	 */
	records: z.array(z.unknown()).nullable().optional(),
});

/**
 * Create Job Info schema
 * Based on POST /jobs request body
 */
export const CreateJobInfoSchema = z.object({
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	typeId: z.string().nullable().optional(),
	organizationId: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	organizationUserIds: z.array(z.string()).nullable().optional(),
	subContractorIds: z.array(z.string()).nullable().optional(),
	latitude: z.number().nullable().optional(),
	longitude: z.number().nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	/** Opaque job-type form values (OpenAPI CreateJobInfo.formData). */
	formData: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Update Job Info schema (EditJobInfo)
 * Based on PUT /jobs/{jobId} request body
 */
export const UpdateJobInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	statusId: z.string().nullable().optional(),
	typeId: z.string().nullable().optional(),
	latitude: z.number().nullable().optional(),
	longitude: z.number().nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	organizationUserIds: z.array(z.string()).nullable().optional(),
	subContractorIds: z.array(z.string()).nullable().optional(),
	/** Opaque job-type form values (OpenAPI EditJobInfo.formData). */
	formData: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Update Job Status Info schema
 * Based on PUT /jobs/{jobId}/status request body
 */
export const UpdateJobStatusInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	statusId: z.string().nullable().optional(),
});

/**
 * Job Parameters schema
 * Used for filtering and querying jobs
 */
export const JobParamsSchema = z.object({
	BoundingBox: BoundingBoxSchema.optional(),
	HasCoordinates: z.boolean().optional(),
	IncludeDeleted: z.boolean().optional(),
	OrganizationId: z.string().uuid().optional(),
	SubOrganizationId: z.string().uuid().optional(),
	RelatedToAssetId: z.string().uuid().optional(),
	RelatedToRecordId: z.string().uuid().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
	SearchTerm: z.string().optional(),
	EntityDataQueries: z.array(DataQuerySchema).optional(),
});

/**
 * Query parameters for GET /jobs/types/count, GET /jobs/types/{start}/{count}, export
 */
export const JobTypesListParamsSchema = z.object({
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().uuid().optional(),
	Enabled: z.boolean().optional(),
	SearchTerm: z.string().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
});

export type JobTypesListParams = z.infer<typeof JobTypesListParamsSchema>;

/** Input for `jobQueryOptions.types()` — supports camelCase org aliases */
export type JobTypesQueryOptionsInput = Partial<JobTypesListParams> & {
	organizationId?: string;
	subOrganizationId?: string;
};

export function normalizeJobTypesQueryOptions(
	input?: JobTypesQueryOptionsInput,
): JobTypesListParams {
	if (!input) return {};
	const { organizationId, subOrganizationId, ...rest } = input;
	const out: JobTypesListParams = { ...rest };
	if (organizationId !== undefined && organizationId !== "") {
		out.OrganizationId = organizationId;
	}
	if (subOrganizationId !== undefined && subOrganizationId !== "") {
		out.SubOrganizationId = subOrganizationId;
	}
	return out;
}

/**
 * Very Limited Organization User With User Display schema
 * Based on VeryLimitedOrganizationUserWithUserDisplay
 */
export const VeryLimitedOrganizationUserWithUserDisplaySchema = z.object({
	email: z.string().nullable().optional(),
	firstName: z.string().nullable().optional(),
	middleName: z.string().nullable().optional(),
	lastName: z.string().nullable().optional(),
	roles: z.string().nullable().optional(),
	id: z.string(),
	userId: z.string().nullable().optional(),
	hasAvatar: z.boolean().optional(),
	displayName: z.string().nullable().optional(),
	title: z.string().nullable().optional(),
	phone: z.string().nullable().optional(),
});

/**
 * Job Assignment Display schema
 * Based on GET /jobs/{jobId}/assignments/{start}/{count}
 */
export const JobAssignmentDisplaySchema = z.object({
	id: z.string().uuid(),
	jobId: z.string().uuid().optional(),
	organizationUserId: z.string().uuid().optional(),
	organizationUser:
		VeryLimitedOrganizationUserWithUserDisplaySchema.nullish().optional(),
});

/**
 * Job Assignment Display array schema
 */
export const JobAssignmentDisplayArraySchema = z.array(
	JobAssignmentDisplaySchema,
);

/**
 * Job Assignment Parameters schema
 */
export const JobAssignmentParamsSchema = z.object({
	JobId: z.string().uuid().optional(),
	OrganizationUserId: z.string().uuid().optional(),
	OrganizationId: z.string().uuid().optional(),
	SubOrganizationId: z.string().uuid().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
});

/**
 * Sub-Contractor Display schema
 * Based on GET /jobs/{jobId}/sub-contractors/{start}/{count}
 */
export const SubContractorDisplaySchema = z.object({
	id: z.string(),
	name: z.string().nullable().optional(),
	phone: z.string().nullable().optional(),
	totalAssignedUserCount: z.number().int().nullable().optional(),
	statusCode: z.number().int().nullable().optional(),
	address: SimpleAddressSchema.nullable().optional(),
	created: z.string().datetime().nullable().optional(),
	lastUpdated: z.string().datetime().nullable().optional(),
});

/**
 * Sub-Contractor Parameters schema
 */
export const SubContractorParamsSchema = z.object({
	JobId: z.string().uuid().optional(),
	SubContractorId: z.string().uuid().optional(),
	OrganizationId: z.string().uuid().optional(),
	SubOrganizationId: z.string().uuid().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
});

/**
 * Feature Collection schema (GeoJSON)
 * Based on GET /jobs/features/{start}/{count}
 */
export const FeatureCollectionSchema = z.object({
	type: z.literal("FeatureCollection"),
	features: z.array(
		z.object({
			type: z.literal("Feature"),
			geometry: z.unknown(), // GeoJSON geometry
			properties: z.unknown(), // Job properties
		}),
	),
});

// ==================== AUDIT LOG / HISTORY SCHEMAS ====================

/**
 * Job display with audit users — GET /jobs/{id}
 */
export const JobDisplayWithUsersSchema = JobDisplaySchema.extend({
	createdBy: UserInfoLightSchema.nullable().optional(),
	updatedBy: UserInfoLightSchema.nullable().optional(),
});

/**
 * Organization display schema (simplified for audit log)
 */
export const OrganizationDisplayLightSchema = z.object({
	id: z.string().uuid().nullable().optional(),
	name: z.string().nullable().optional(),
});

/**
 * Audit log display schema
 * Based on GET /jobs/{id}/history/{start}/{count}
 */
export const AuditLogDisplaySchema = z.object({
	id: z.string().uuid(),
	organizationId: z.string().nullable().optional(),
	userId: z.string().nullable().optional(),
	action: z.string().nullable().optional(),
	date: z.string().datetime().nullable().optional(),
	source: z.string().nullable().optional(),
	entityType: z.string().nullable().optional(),
	entityId: z.string().nullable().optional(),
	entityDisplay: z.string().nullable().optional(),
	fieldName: z.string().nullable().optional(),
	sessionId: z.string().nullable().optional(),
	oldValue: z.string().nullable().optional(),
	newValue: z.string().nullable().optional(),
	userAgent: z.string().nullable().optional(),
	userIpAddress: z.string().nullable().optional(),
	user: UserInfoLightSchema.nullable().optional(),
	organization: OrganizationDisplayLightSchema.nullable().optional(),
});

// ==================== TYPE EXPORTS ====================

export type JobStatus = z.infer<typeof JobStatusSchema>;
export type JobStatusDisplay = z.infer<typeof JobStatusDisplaySchema>;
export type GetJobStatusesResponse = JobStatusDisplay[];
export type BoundingBox = z.infer<typeof BoundingBoxSchema>;
export type JobTypeDisplay = z.infer<typeof JobTypeDisplaySchema>;
/** Row type for job type lists and dropdowns */
export type JobType = JobTypeDisplay;
export type CreateJobTypeInfo = z.infer<typeof CreateJobTypeInfoSchema>;
export type EditJobTypeInfo = z.infer<typeof EditJobTypeInfoSchema>;
export type JobUserReference = z.infer<typeof JobUserReferenceSchema>;
export type JobDisplay = z.infer<typeof JobDisplaySchema>;
export type JobDisplayWithUsers = z.infer<typeof JobDisplayWithUsersSchema>;
export type CreateJobInfo = z.infer<typeof CreateJobInfoSchema>;
export type UpdateJobInfo = z.infer<typeof UpdateJobInfoSchema>;
export type UpdateJobStatusInfo = z.infer<typeof UpdateJobStatusInfoSchema>;
export type JobParams = z.infer<typeof JobParamsSchema>;
export type VeryLimitedOrganizationUserWithUserDisplay = z.infer<
	typeof VeryLimitedOrganizationUserWithUserDisplaySchema
>;
export type JobAssignmentDisplay = z.infer<typeof JobAssignmentDisplaySchema>;
export type JobAssignmentDisplayArray = z.infer<
	typeof JobAssignmentDisplayArraySchema
>;
export type JobAssignmentParams = z.infer<typeof JobAssignmentParamsSchema>;
export type SubContractorDisplay = z.infer<typeof SubContractorDisplaySchema>;
export type SubContractorParams = z.infer<typeof SubContractorParamsSchema>;
export type FeatureCollection = z.infer<typeof FeatureCollectionSchema>;
export type OrganizationDisplayLight = z.infer<
	typeof OrganizationDisplayLightSchema
>;
export type AuditLogDisplay = z.infer<typeof AuditLogDisplaySchema>;

// ==================== JOB ASSET / RECORD ASSOCIATIONS ====================

/**
 * OpenAPI `JobAssetInfo` — junction row for job↔asset links.
 * Swagger incorrectly types list responses as NoteDisplay[]; use this instead.
 */
export const JobAssetInfoSchema = z.object({
	id: z.string().nullable().optional(),
	serverCreatedAt: z.string().nullable().optional(),
	serverUpdatedAt: z.string().nullable().optional(),
	organizationId: z.string().uuid().nullable().optional(),
	jobId: z.string().uuid().nullable().optional(),
	assetId: z.string().uuid().nullable().optional(),
});

export const JobAssetInfoArraySchema = z.array(JobAssetInfoSchema);

/**
 * OpenAPI `JobRecordInfo` — junction row for job↔record links.
 */
export const JobRecordInfoSchema = z.object({
	id: z.string().nullable().optional(),
	serverCreatedAt: z.string().nullable().optional(),
	serverUpdatedAt: z.string().nullable().optional(),
	organizationId: z.string().uuid().nullable().optional(),
	jobId: z.string().uuid().nullable().optional(),
	recordId: z.string().uuid().nullable().optional(),
});

export const JobRecordInfoArraySchema = z.array(JobRecordInfoSchema);

/**
 * Query filters for job asset association list / count endpoints.
 */
export const JobAssetAssociationParamsSchema = z.object({
	JobId: z.string().optional(),
	AssetId: z.string().optional(),
	OrganizationUserId: z.string().optional(),
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().optional(),
	OrderBy: z
		.object({
			Fields: z.array(z.string()).optional(),
			Descending: z.boolean().optional(),
		})
		.optional(),
});

/**
 * Query filters for job record association list / count endpoints.
 */
export const JobRecordAssociationParamsSchema = z.object({
	JobId: z.string().optional(),
	RecordId: z.string().optional(),
	OrganizationUserId: z.string().optional(),
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().optional(),
	OrderBy: z
		.object({
			Fields: z.array(z.string()).optional(),
			Descending: z.boolean().optional(),
		})
		.optional(),
});

export type JobAssetInfo = z.infer<typeof JobAssetInfoSchema>;
export type JobRecordInfo = z.infer<typeof JobRecordInfoSchema>;
export type JobAssetAssociationParams = z.infer<
	typeof JobAssetAssociationParamsSchema
>;
export type JobRecordAssociationParams = z.infer<
	typeof JobRecordAssociationParamsSchema
>;

// ==================== JOB FILES ====================

/**
 * OpenAPI `FileDisplay` — GET /{entity}/{id}/files/{start}/{count}
 */
export const FileDisplaySchema = z.object({
	id: z.string().uuid(),
	/** Underlying FileInfo id — use for GET/DELETE/HEAD/PATCH/thumbnail. */
	fileInfoId: z.string().uuid(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	size: z.number().int().nullable().optional(),
	createdAt: z.string().nullable().optional(),
	/** Display name of the user who uploaded the file (API PR 1034). */
	createdByUser: z.string().nullable().optional(),
	entityAttachmentRequirementTagId: z.string().uuid().nullable().optional(),
});

export const FileDisplayArraySchema = z.array(FileDisplaySchema);

/**
 * OpenAPI `EditJobFileInfo` — PUT /jobs/{jobId}/files/{jobFileId}
 */
export const EditJobFileInfoSchema = z.object({
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
});

/**
 * Query filters for job file list / count endpoints.
 */
export const JobFileParamsSchema = z.object({
	SearchTerm: z.string().optional(),
	IncludeDeleted: z.boolean().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	OrderBy: z
		.object({
			Fields: z.array(z.string()).optional(),
			Descending: z.boolean().optional(),
		})
		.optional(),
});

export type FileDisplay = z.infer<typeof FileDisplaySchema>;
export type EditJobFileInfo = z.infer<typeof EditJobFileInfoSchema>;
export type JobFileParams = z.infer<typeof JobFileParamsSchema>;

// ==================== ERROR TYPES ====================

/**
 * Custom error class for job-related API errors
 */
export class JobApiError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
		public readonly statusCode?: number,
	) {
		super(message);
		this.name = "JobApiError";
		// Maintains proper stack trace for where our error was thrown (only available on V8)
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, JobApiError);
		}
	}

	static fromServiceResult(result: {
		success: boolean;
		message?: string | null;
	}) {
		return new JobApiError(result.message || "Job operation failed", result);
	}
}

// ==================== SERVICE RESULT ====================

/**
 * Service result schema — supports both the legacy shape
 * (success / message) and the newer shape (isSuccess / errorMessages).
 */
export const ServiceResultSchema = z
	.object({
		success: z.boolean().optional(),
		isSuccess: z.boolean().optional(),
		message: z.string().nullable().optional(),
		errorMessages: z.array(z.string()).nullable().optional(),
		affectedId: z.string().uuid().nullable().optional(),
	})
	.transform((raw) => ({
		success: raw.success ?? raw.isSuccess ?? false,
		message:
			raw.message ?? raw.errorMessages?.filter(Boolean).join("; ") ?? null,
		affectedId: raw.affectedId ?? null,
	}));

export type ServiceResult = z.output<typeof ServiceResultSchema>;
